import { scaleLinear, ScaleLinear } from "d3-scale";
import fromPairs from "lodash-es/fromPairs";
import keyBy from "lodash-es/keyBy";
import max from "lodash-es/max";
import min from "lodash-es/min";
import { extractFromStringProductId } from "../../graphQL/Utils";
import { failIfValidOrNonExhaustive, ProductMetadatumLevel } from "../../Utils";
import { newHSColorMap } from "../../Utils";
import { getIntervalWidener } from "../../viz/Utils";
import {
  ActiveStatus,
  IProcessedEdge,
  IProcessedNode,
  ITooltipNode,
  RelatedNodesMap,
} from "../../workerStore/network/Utils";
import { ExportsChangeNodeSizing } from "../Utils";
import { ProductSpaceResponseDatum, ProductYearDatum } from "./index";

const level = ProductMetadatumLevel.fourDigit;
const widenInterval = getIntervalWidener(1.1);
const defaultNodeRadius = 4.5;

interface NodeWithTradeInput {
  id: string;
  shortName: string;
  longName: string;
  topLevelParent: {
    id: string;
  };
  exportValue: number | null;
  globalExportValue: number | null;
  rca: number;
  pci: number | null;
  code: string;
  x: number;
  y: number;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  nodeRadiusScale: ScaleLinear<number, number>;
}

const appendNodeToMapKey = (
  map: Record<string, Set<number>>,
  key: number,
  value: number
) => {
  if (map[key] === undefined) {
    map[key] = new Set<number>();
  }
  map[key].add(value);
};

interface TransformedData {
  nodeList: IProcessedNode[];
  edgeList: IProcessedEdge[];
  relatedNodesMap: RelatedNodesMap;
  tooltipMap: Record<string, ITooltipNode>;
}

const transform = (
  productSpace: ProductSpaceResponseDatum[],
  productYear: ProductYearDatum[],
  height: number,
  width: number,
  showAllProducts: boolean,
  nodeSizing: ExportsChangeNodeSizing
): TransformedData => {
  const getNodeWithTrade = (input: NodeWithTradeInput) => {
    const {
      shortName,
      longName,
      xScale,
      yScale,
      topLevelParent,
      globalExportValue,
      rca,
      code,
      x,
      y,
      nodeRadiusScale,
      exportValue,
      pci,
      id,
    } = input;

    const sectorColor = newHSColorMap.get(topLevelParent.id);
    const worldTrade = globalExportValue !== null ? globalExportValue : 0;
    const countryTrade = exportValue !== null ? exportValue : 0;
    const color = sectorColor !== undefined ? sectorColor : "rgba(0, 0, 0, 0)";
    const active =
      rca < 1 && showAllProducts === false
        ? ActiveStatus.NotExported
        : ActiveStatus.Active;
    let radius: number;
    if (nodeSizing === ExportsChangeNodeSizing.WorldTrade) {
      radius = nodeRadiusScale(worldTrade);
    } else if (nodeSizing === ExportsChangeNodeSizing.CountryTrade) {
      radius = nodeRadiusScale(countryTrade);
    } else if (nodeSizing === ExportsChangeNodeSizing.None) {
      radius = defaultNodeRadius;
    } else {
      failIfValidOrNonExhaustive(
        nodeSizing,
        "Invalid value for nodeSizing " + nodeSizing
      );
      radius = defaultNodeRadius;
    }
    return {
      id: extractFromStringProductId(id).id,
      x,
      y,
      shortLabel: shortName,
      longLabel: longName,
      scaledX: xScale(x),
      scaledY: yScale(y),
      section: extractFromStringProductId(topLevelParent.id).id,
      code,
      color,
      uniqueKey: `${code}${longName}${x * y}`,
      level,
      countryTrade,
      worldTrade,
      rca,
      pci,
      active,
      radius,
    };
  };

  const allXValues: number[] = [];
  const allYValues: number[] = [];
  const allWorldTrade: Array<number | null> = [];
  const allCountryTrade: Array<number | null> = [];

  productSpace.forEach(({ x, y, product: { id }, exportValue }) => {
    const targetProduct = productYear.find((d) => d.product.id === id);
    allXValues.push(x);
    allYValues.push(y);
    allWorldTrade.push(
      targetProduct && targetProduct.globalExportValue
        ? targetProduct.globalExportValue
        : 0
    );
    allCountryTrade.push(exportValue);
  });

  const minX = min(allXValues);
  const maxX = max(allXValues);
  const minY = min(allYValues);
  const maxY = max(allYValues);

  const minTradeVal =
    nodeSizing === ExportsChangeNodeSizing.WorldTrade
      ? min(allWorldTrade)
      : min(allCountryTrade);
  const maxTradeVal =
    nodeSizing === ExportsChangeNodeSizing.WorldTrade
      ? max(allWorldTrade)
      : max(allCountryTrade);

  const minNodeRadius = defaultNodeRadius;
  const maxNodeRadius = 20;

  if (
    minX !== undefined &&
    maxX !== undefined &&
    minY !== undefined &&
    maxY !== undefined &&
    minTradeVal !== null &&
    minTradeVal !== undefined &&
    maxTradeVal !== null &&
    maxTradeVal !== undefined &&
    minNodeRadius !== null &&
    maxNodeRadius !== null &&
    minNodeRadius !== undefined &&
    maxNodeRadius !== undefined
  ) {
    const xScale = scaleLinear<number, number>()
      .domain(widenInterval(minX, maxX))
      .range([0, width]);
    const yScale = scaleLinear<number, number>()
      .domain(widenInterval(minY, maxY))
      .range([height, 0]);
    const nodeRadiusScale = scaleLinear<number, number>()
      .domain([minTradeVal, maxTradeVal])
      .range([minNodeRadius, maxNodeRadius]);

    const nodeMap: Map<number, IProcessedNode> = new Map();
    const nodeList: IProcessedNode[] = [];

    const edgeMap: Record<string, Set<number>> = {};
    const edgeList: IProcessedEdge[] = [];

    const tooltipMapDataList: ITooltipNode[] = [];

    productSpace.forEach((node) => {
      const {
        product: { id: sourceId, ...product },
        x,
        y,
        rca,
        exportValue,
      } = node;
      const targetProduct = productYear.find((d) => d.product.id === sourceId);

      const nodeWithTradeData = getNodeWithTrade({
        id: sourceId,
        ...product,
        exportValue,
        x,
        y,
        rca,
        xScale,
        yScale,
        nodeRadiusScale,
        globalExportValue:
          targetProduct && targetProduct.globalExportValue
            ? targetProduct.globalExportValue
            : 0,
        pci: targetProduct && targetProduct.pci ? targetProduct.pci : 0,
      });
      const newNode = { ...nodeWithTradeData, connections: [] };
      nodeMap.set(extractFromStringProductId(sourceId).id, newNode);
      nodeList.push(newNode);

      const numberId = extractFromStringProductId(sourceId).id;

      const tooltipNode = nodeMap.get(numberId);

      let tooltipOutput: ITooltipNode;
      if (tooltipNode !== undefined) {
        tooltipOutput = {
          shortLabel: product.shortName,
          code: product.code,
          scaledX: tooltipNode.scaledX,
          scaledY: tooltipNode.scaledY,
          detailOverlayInfo: [],
          id: numberId,
          connections: tooltipNode.connections,
          longLabel: product.longName,
          color: tooltipNode.color,
          level,
        };
      } else {
        tooltipOutput = {
          shortLabel: product.shortName,
          code: product.code,
          scaledX: 0,
          scaledY: 0,
          detailOverlayInfo: [],
          id: numberId,
          connections: [],
          longLabel: product.longName,
          color: "rgba(0, 0, 0, 0)",
          level,
        };
      }
      tooltipMapDataList.push(tooltipOutput);
    });

    const outputPairs: Array<[string, number[]]> = Object.entries(edgeMap).map(
      ([key, valuesSet]) => [key, [...valuesSet]] as [string, number[]]
    );
    const relatedNodesMap: Record<string, number[]> = fromPairs(outputPairs);

    const tooltipMap: Record<string, ITooltipNode> = keyBy(
      tooltipMapDataList,
      ({ id }) => id
    );

    return {
      nodeList,
      edgeList,
      relatedNodesMap,
      tooltipMap,
    };
  }
  return {
    nodeList: [],
    edgeList: [],
    relatedNodesMap: {},
    tooltipMap: {},
  };
};

export default transform;
